import {
  CargoPumpMeterFormState,
  FormSectionStateArray,
  FormState,
  MachineryRhFormState,
  PowerVm,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { isStateField } from '../vm-builder-helpers';
import { CalculatedValue } from '../with-calculated-values';

// The Calculated Values of a section that holds each one in its own field.
const sectionValues = (sectionId: string, values: Record<string, number | undefined>): CalculatedValue[] =>
  Object.entries(values).map(([fieldId, value]) => ({ sectionId, fieldId, value }));

// The rows of a row section, or no rows when the section holds none.
const rowsOf = (formState: FormState, sectionId: string, fieldId: string): FormSectionStateArray => {
  const rows = formState[sectionId]?.[fieldId];
  return !rows || isStateField(rows) ? [] : rows;
};

const mainEngineValues = (vm: PowerVm, tcRpmPower: number | undefined) =>
  sectionValues('mainEngine', {
    mainEngineLoad: vm.mainEngineLoad,
    mainEngineRevCounterDiff: vm.mainEngineRevCounterDiff,
    mainEngineCalculatedRpm: vm.mainEngineCalculatedRpm,
    mainEngines1And2Power: vm.mainEngines1And2Power,
    mainEngines1And2Load: vm.mainEngines1And2Load,
    mainEngines1And2RunningHours: vm.mainEngines1And2RunningHours,
    shaftPowerMeterCounterDifference: vm.shaftPowerMeterCounterDiff,
    shaftPowerMeterCounterCalculated: vm.shaftPowerMeterCounterCalculated,
    tcRpmPower,
  });

// Each row group of the section, for example mainEngines. The template holds
// one field for each row, named from the row key, for example
// mainEngine1Difference.
const machineryRhValues = (formState: FormState, vm: PowerVm): CalculatedValue[] =>
  Object.keys(formState['machineryRunningHours'] ?? {}).flatMap((fieldId) =>
    rowsOf(formState, 'machineryRunningHours', fieldId).map((row, index) => ({
      sectionId: 'machineryRunningHours',
      fieldId,
      index,
      property: 'difference',
      value: vm.getRhDiff(row as MachineryRhFormState),
      rule: { sectionId: 'machineryRunningHours', fieldId: `${row['key'].value}Difference` },
    }))
  );

const flowMeterOldValues = (vm: PowerVm) =>
  sectionValues('flowMeterInstallationOld', {
    foBoosterDiff: vm.foBoosterDiff,
    gasOilBoosterDiff: vm.gasOilBoosterDiff,
    meAeInletDiff: vm.meAeInletDiff,
    auxEngineInletDiff: vm.auxEngineInletDiff,
    auxEngineOutletDiff: vm.auxEngineOutletDiff,
    auxBoiler1Diff: vm.auxBoiler1Diff,
    cylinderLubeOilDiff: vm.cylinderLubeOilDiff,
  });

// The vessel spec names the state path of each meter reading.
const flowMeterInstallationValues = (vm: PowerVm) =>
  sectionValues(
    'flowMeterInstallation',
    Object.fromEntries(
      [...vm.mainFlowMeters, ...vm.boilerFlowMeters, ...vm.mainGasFlowMeters, ...vm.boilerGasFlowMeters]
        .filter((meter) => meter.diff_flow_path)
        .map((meter) => [
          meter.diff_flow_path,
          vm.getFlowMeterInstallationDiff(meter.current_flow_path, meter.prev_flow_path),
        ])
    )
  );

const pumpValues = (vm: PowerVm) =>
  sectionValues('pumps', {
    meLoPump1Diff: vm.meLoPump1Diff,
    meLoPump2Diff: vm.meLoPump2Diff,
    seaWaterPump1Diff: vm.seaWaterPump1Diff,
    seaWaterPump2Diff: vm.seaWaterPump2Diff,
    sgPump1Diff: vm.sgPump1Diff,
    sgPump2Diff: vm.sgPump2Diff,
    foPurifier1Diff: vm.foPurifier1Diff,
    foPurifier2Diff: vm.foPurifier2Diff,
    erFan1Diff: vm.erFan1Diff,
    erFan2Diff: vm.erFan2Diff,
    erFan3Diff: vm.erFan3Diff,
    erFan4Diff: vm.erFan4Diff,
  });

// The template holds one field for each row, named from the row key, for
// example auxEngine2MaxRating. The import holds a row only for an aux engine
// with data, so the row index is not the engine number.
const generatorEngineValues = (formState: FormState, vm: PowerVm): CalculatedValue[] =>
  rowsOf(formState, 'generatorEngines', 'auxEngines').flatMap((row, index) => {
    const rowKey = row['key'].value as string;
    const rowValue = (property: string, ruleSuffix: string, value: number | undefined): CalculatedValue => ({
      sectionId: 'generatorEngines',
      fieldId: 'auxEngines',
      index,
      property,
      value,
      rule: { sectionId: 'generatorEngines', fieldId: `${rowKey}${ruleSuffix}` },
    });

    return [
      rowValue('maxRating', 'MaxRating', vm.getGeMaxRating(rowKey)),
      rowValue('runningHours', 'RunningHours', vm.getGeRunningHours(rowKey)),
    ];
  });

const shaftGeneratorValues = (vm: PowerVm) =>
  sectionValues('shaftGeneratorPTO', { maxRating: vm.getSgMaxRating(), runningHours: vm.getSgRunningHours() });

// The template holds no field for the Difference of a cargo pump meter, so no
// rule hides it or requires it. Its section rule still applies.
const cargoPumpMeterValues = (formState: FormState, vm: PowerVm): CalculatedValue[] =>
  rowsOf(formState, 'cargoPumpMeters', 'pumps').map((row, index) => ({
    sectionId: 'cargoPumpMeters',
    fieldId: 'pumps',
    index,
    property: 'diff',
    value: vm.getCargoPumpMeterDiff(row as CargoPumpMeterFormState),
  }));

/**
 * Every Calculated Value of the Power tab. `withCalculatedValues` writes each
 * one to the form state, and ReportStore sets its status.
 *
 * `tcRpmPower` is the value that the server last gave. See
 * `withTcRpmPowerReducer`.
 */
export const collectPowerCalculatedValues = (
  formState: FormState,
  vm: PowerVm,
  tcRpmPower: number | undefined
): CalculatedValue[] => [
  ...mainEngineValues(vm, tcRpmPower),
  ...machineryRhValues(formState, vm),
  ...flowMeterOldValues(vm),
  ...flowMeterInstallationValues(vm),
  ...pumpValues(vm),
  ...generatorEngineValues(formState, vm),
  ...shaftGeneratorValues(vm),
  ...cargoPumpMeterValues(formState, vm),
  ...sectionValues('shorePower', { hours: vm.shorePowerHours }),
];
